/*
 * A home's size from the answers it was quoted from (owner, 2026-11-02: the crew want to see the bedrooms, bathrooms and
 * other rooms or square feet on the job so they know what is part of it, and the office can offer a repeat clean fast).
 * Two shapes hold it: the price list's answers (HomeAnswers) and the old website's quote form ({ webJob: ... }).
 */

export interface HomeSize {
  beds: number | null;
  baths: number | null;
  /** Rooms past the bedrooms and bathrooms (an office, a game room). */
  rooms: number | null;
  sqft: number | null;
}

const count = (v: unknown, decimal = false): number | null => {
  const n = typeof v === "string" && v.trim() ? Number(v) : v;
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return null;
  return decimal ? Math.round(n * 2) / 2 : Math.trunc(n);
};

/** The size in a quote's or job line's answers, or null when they hold none. */
export function homeSizeFromAnswers(raw: unknown): HomeSize | null {
  if (!raw || typeof raw !== "object") return null;
  const top = raw as Record<string, unknown>;
  const a = (top.webJob && typeof top.webJob === "object" ? top.webJob : top) as Record<string, unknown>;
  if (a.kind === "lawn") return null;
  // Priced by square feet, the room counts are only the form's defaults, never what the client told us (owner, Nov 2:
  // Norah gave 2,200 sq ft, not 2 bed, 2 bath and 5 rooms). Priced by the room, the square feet field was never asked.
  const bySqft = (a.sizeBy ?? a.sizeMode) === "sqft";
  const size: HomeSize = bySqft
    ? { beds: null, baths: null, rooms: null, sqft: count(a.sqft) }
    : { beds: count(a.beds), baths: count(a.baths, true), rooms: count(a.other), sqft: null };
  return Object.values(size).some((v) => v !== null) ? size : null;
}

const plural = (n: number, one: string, many: string) => `${String(n)} ${n === 1 ? one : many}`;

/** "2 bed, 2 bath, 5 other rooms, 2,200 sq ft", or null when nothing is known. */
export function homeWords(s: Partial<HomeSize>): string | null {
  const parts: string[] = [];
  if (s.beds != null) parts.push(`${String(s.beds)} bed`);
  if (s.baths != null) parts.push(`${String(s.baths)} bath`);
  if (s.rooms != null) parts.push(plural(s.rooms, "other room", "other rooms"));
  if (s.sqft != null) parts.push(`${s.sqft.toLocaleString("en-US")} sq ft`);
  return parts.length ? parts.join(", ") : null;
}

/**
 * The home's size in the crew member's language (owner, 2026-11-02: Spanish crew saw "2 bed, 1 bath" in English).
 * English reads as homeWords; Spanish: "2 recámaras, 1 baño, 5 cuartos más, 2,200 pies cuadrados". A business has only
 * rooms ("12 cuartos"); a lawn its grass ("4,000 pies cuadrados de pasto").
 */
export function homeWordsIn(
  s: Partial<HomeSize> & { lawnSqft?: number | null },
  locale: "en" | "es",
  business = false,
): string | null {
  const feet = (n: number) => n.toLocaleString("en-US");
  if (locale === "en") {
    const parts = [business && s.rooms != null ? `${String(s.rooms)} rooms` : null];
    const home = homeWords(business ? { ...s, rooms: null } : s);
    if (home) parts.push(home);
    if (s.lawnSqft != null) parts.push(`${feet(s.lawnSqft)} sq ft lawn`);
    const out = parts.filter(Boolean).join(", ");
    return out || null;
  }
  const many = (n: number, one: string, more: string) => `${String(n)} ${n === 1 ? one : more}`;
  const parts: string[] = [];
  if (s.beds != null) parts.push(many(s.beds, "recámara", "recámaras"));
  if (s.baths != null) parts.push(many(s.baths, "baño", "baños"));
  if (s.rooms != null) parts.push(business ? many(s.rooms, "cuarto", "cuartos") : many(s.rooms, "cuarto más", "cuartos más"));
  if (s.sqft != null) parts.push(`${feet(s.sqft)} pies cuadrados`);
  if (s.lawnSqft != null) parts.push(`${feet(s.lawnSqft)} pies cuadrados de pasto`);
  return parts.length ? parts.join(", ") : null;
}
